import { Button } from "@community-project-org/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { JSX } from "react";
import { useNavigate } from "react-router-dom";

import { apiRequest, ApiRequestError } from "../../common/api/apiClient.js";
import { Money } from "../../common/components/Money.js";
import { SeatMap, seatKey, type SeatCell } from "../../common/components/SeatMap.js";
import { Stepper } from "../../common/components/Stepper.js";
import type { components } from "../../generated/api.js";

type ServiceDTO = components["schemas"]["ServiceDTO"];
type SeatAvailabilityDTO = components["schemas"]["SeatAvailabilityDTO"];
type BookingDTO = components["schemas"]["BookingDTO"];

/** Mehr Tickets in einer Buchung braucht ein Gast selten; Gruppen buchen über den Anbieter. */
const MAX_PER_BOOKING = 10;

/**
 * Buchung eines Events: mit Sitzplan Plätze wählen, sonst eine Anzahl aus dem
 * Kontingent. Was frei ist, sagt der Server; ob es beim Absenden noch frei ist, auch
 * — bis dahin bleibt die Schaltfläche im Ladezustand.
 */
export function EventBooking({ offer }: { offer: ServiceDTO }): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Map<string, SeatCell>>(new Map());
  const [quantity, setQuantity] = useState(1);

  const availability = useQuery({
    queryKey: ["offer-seats", offer.id],
    queryFn: () =>
      apiRequest<SeatAvailabilityDTO>(`/offers/${encodeURIComponent(offer.id)}/seats`, {
        tenant: null,
      }),
  });

  const book = useMutation({
    mutationFn: () =>
      apiRequest<BookingDTO>("/bookings", {
        method: "POST",
        body: offer.seatSelection
          ? {
              serviceId: offer.id,
              seats: [...selected.values()].map(({ row, number }) => ({ row, number })),
            }
          : { serviceId: offer.id, quantity },
        tenant: null,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
      navigate("/tickets");
    },
    onError: async (error) => {
      // Vergeben oder ausverkauft: der Stand ist veraltet. Neu laden statt raten.
      if (error instanceof ApiRequestError && error.status === 409) {
        setSelected(new Map());
        await availability.refetch();
      }
    },
  });

  if (availability.isPending) {
    return <p className="text-[14px] text-ink-3">Verfügbarkeit wird geladen …</p>;
  }
  if (availability.isError) {
    return <p className="text-[14px] text-danger">Die Verfügbarkeit ist gerade nicht erreichbar.</p>;
  }

  const data = availability.data;
  const remaining = Math.max(0, data.capacity - data.taken);
  const count = offer.seatSelection ? selected.size : quantity;
  const max = Math.min(MAX_PER_BOOKING, offer.seatSelection ? MAX_PER_BOOKING : remaining);

  if (!offer.seatSelection && offer.capacity !== null && remaining === 0) {
    return <p className="text-[14px] font-bold text-ink-2">Ausverkauft.</p>;
  }

  const toggle = (seat: SeatCell): void => {
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(seatKey(seat))) {
        next.delete(seatKey(seat));
      } else if (next.size < MAX_PER_BOOKING) {
        next.set(seatKey(seat), seat);
      }
      return next;
    });
  };

  return (
    <div className="grid gap-4">
      {offer.seatSelection ? (
        <SeatMap seats={data.seats} selected={new Set(selected.keys())} onToggle={toggle} max={MAX_PER_BOOKING} />
      ) : (
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[14px] font-bold text-ink">Anzahl</p>
            {offer.capacity !== null && (
              <p className="text-[12.5px] text-ink-3">
                noch <span className="num">{remaining}</span> verfügbar
              </p>
            )}
          </div>
          <Stepper
            label="Anzahl"
            value={quantity}
            min={1}
            max={offer.capacity === null ? MAX_PER_BOOKING : max}
            onChange={setQuantity}
          />
        </div>
      )}

      {offer.seatSelection && selected.size > 0 && (
        <p className="num text-[13px] font-bold text-ink-2">
          {[...selected.keys()].join(", ")}
        </p>
      )}

      {book.error && (
        <p className="text-[13px] text-danger" role="alert">
          {book.error instanceof ApiRequestError ? book.error.message : "Das hat nicht geklappt."}
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        <span className="text-[17px] font-extrabold text-ink">
          <Money cents={offer.priceCents * count} currency={offer.currency} />
        </span>
        <Button disabled={count === 0 || book.isPending} aria-busy={book.isPending} onClick={() => book.mutate()}>
          {book.isPending ? "Wird gebucht …" : count === 1 ? "1 Ticket buchen" : `${count} Tickets buchen`}
        </Button>
      </div>
    </div>
  );
}
